import type { Patch } from "@freightsimple/shared/types";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { FormBox } from "@freightsimple/ui-core/FormLayout/FormBox";
import { FormGroup } from "@freightsimple/ui-core/FormLayout/FormGroup";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { NumberInput } from "@freightsimple/ui-core/Controls/NumberInput";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { PencilSimpleIcon } from "@phosphor-icons/react/PencilSimple";
import { dangerousGoodsDescribeHazardClass } from "@/Shared/DangerousGoods/describeHazardClass";
import { dangerousGoodsIsClass1Explosive } from "../../Helpers/dangerousGoodsIsClass1Explosive";
import { dangerousGoodsRequiresTechnicalName } from "../../Helpers/dangerousGoodsRequiresTechnicalName";
import { maxDangerousNetExplosiveQuantityKg } from "../../Validators/dangerousGoodsLimits";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";
import { LineItemsNumberPackagesQuestionBubble } from "@/Shared/LineItems/Bubbles/LineItemsNumberPackagesQuestionBubble";
import { LineItemsPackagingTypeQuestionBubble } from "@/Shared/LineItems/Bubbles/LineItemsPackagingTypeQuestionBubble";
import { LineItemsTechnicalNameQuestionBubble } from "@/Shared/LineItems/Bubbles/LineItemsTechnicalNameQuestionBubble";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import { useState } from "react";

interface EnterDetailsTabContentProps {
  lineItem: Patch<LineItem>;

  forceValidation: boolean;
  onChange: (updates: Patch<LineItem>) => void;

  onChangeUnNumber: () => void;

  // The extra dangerous-goods rules come from Canadian TDG, so they only apply
  // when the shipment touches Canada: net explosive quantity for Class 1, and a
  // mandatory technical name for special provision 16 UN numbers.
  canadianTdgRules: boolean;

  // Served field bounds - the backend's ShipmentLimits is the source of truth.
  shipmentLimits: ShipmentLimits;
}

export function EnterDetailsTabContent(props: EnterDetailsTabContentProps) {
  const {
    dangerousUnNumber,
    dangerousClassification,
    dangerousPackingGroup,
    dangerousProperShippingName,
    dangerousTechnicalName,
    dangerousNumberPackages,
    dangerousPackagingType,
    dangerousNetExplosiveQuantityKg,
  } = props.lineItem;

  // The UN list says one of three things about a packing group, and they don't
  // mean the same thing:
  //
  //   a group ("II")  a fact we looked up
  //   key absent      this class does carry packing groups, the list just has
  //                   none for this entry — so the customer has to supply it
  //   empty string    the substance has no packing group at all (classes 2, 7,
  //                   9 and the forbidden entries; not one of the 231 class-2
  //                   entries in the list carries one)
  //
  // Only the absent case is a question for the customer. Asking in the empty
  // case would invite them to invent a packing group, and it goes onto the
  // carrier paperwork from there. The backend draws the same line —
  // AmyBookPickupRequestEmailValidator passes on isNullOrBlank().
  //
  // Snapshotted on mount so a group the customer picks doesn't make the field
  // jump out of the form and into the summary mid-edit.
  const [packingGroupSource] = useState<
    "lookedUp" | "notApplicable" | "askCustomer"
  >(function () {
    const packingGroup = props.lineItem.dangerousPackingGroup;
    if (packingGroup === undefined) {
      return "askCustomer";
    }
    return packingGroup === "" ? "notApplicable" : "lookedUp";
  });

  // Net explosive quantity only applies to Class 1 explosives.
  const collectNetExplosiveQuantity =
    props.canadianTdgRules &&
    dangerousGoodsIsClass1Explosive(dangerousClassification);

  // Special provision 16 makes the technical name mandatory for this UN number.
  // It is a TDG rule, so it applies on the same shipments the other ones do.
  const technicalNameRequired =
    props.canadianTdgRules &&
    dangerousGoodsRequiresTechnicalName(dangerousUnNumber);

  const maxNumberPackages =
    props.shipmentLimits.dangerousGoods.maxNumberPackages;

  function errorMessageForNumberPackages() {
    if (dangerousNumberPackages === undefined) {
      return "Required";
    }
    // Typing outside the range is refused below, but a value can still arrive
    // from a saved draft or commodity.
    if (dangerousNumberPackages < 1) {
      return "Must be 1 or more";
    }
    if (dangerousNumberPackages > maxNumberPackages) {
      return `Must be ${maxNumberPackages.toLocaleString()} or fewer`;
    }
    return undefined;
  }

  function errorMessageForPackagingType() {
    if (dangerousPackagingType === undefined) {
      return "Required";
    }
    const maxLength =
      props.shipmentLimits.dangerousGoods.maxPackagingTypeLength;
    if (dangerousPackagingType.length > maxLength) {
      return `Must be ${maxLength} characters or fewer`;
    }
    return undefined;
  }

  function errorMessageForTechnicalName() {
    if (technicalNameRequired && (dangerousTechnicalName ?? "").trim() === "") {
      return "Required for this UN number";
    }
    const maxLength =
      props.shipmentLimits.dangerousGoods.maxTechnicalNameLength;
    if (
      dangerousTechnicalName !== undefined &&
      dangerousTechnicalName.length > maxLength
    ) {
      return `Must be ${maxLength} characters or fewer`;
    }
    return undefined;
  }

  function errorMessageForPackingGroup() {
    if (dangerousPackingGroup === undefined || dangerousPackingGroup === "") {
      return "Required";
    }
    return undefined;
  }

  const maxNetExplosiveQuantityKg = maxDangerousNetExplosiveQuantityKg(
    props.lineItem,
    props.shipmentLimits.dangerousGoods.maxNetExplosiveQuantityKg,
  );

  function errorMessageForNetExplosiveQuantity() {
    if (!collectNetExplosiveQuantity) {
      return undefined;
    }
    if (dangerousNetExplosiveQuantityKg === undefined) {
      return "Required";
    }
    if (dangerousNetExplosiveQuantityKg <= 0) {
      return "Must be greater than zero";
    }
    if (dangerousNetExplosiveQuantityKg > maxNetExplosiveQuantityKg) {
      const servedMax =
        props.shipmentLimits.dangerousGoods.maxNetExplosiveQuantityKg;
      return maxNetExplosiveQuantityKg < servedMax
        ? `Can't exceed the item's total weight (${maxNetExplosiveQuantityKg} kg)`
        : `Must be ${servedMax} kg or less`;
    }
    return undefined;
  }

  return (
    <ShowFormErrors show={props.forceValidation}>
      <Stack gap={4}>
        {/* Everything the UN lookup settled, collapsed into one read-only
            block. Changing any of it means picking a different UN number, so
            the block carries a single action back to the lookup step rather
            than a control per field. */}
        <FormBox variant="filled">
          <Row justify="between" align="start" gap={3} grow>
            <Stack gap={1}>
              <Row gap={2} align="baseline" wrap shrinkable>
                <T size="xl" weight="bold" testId="dg-un-number" nowrap>
                  {dangerousUnNumber}
                </T>
                <T
                  size="base"
                  weight="medium"
                  testId="dg-proper-shipping-name"
                  color="secondary"
                >
                  {dangerousProperShippingName}
                </T>
              </Row>
              <Row gap={2} align="baseline" wrap shrinkable>
                <T
                  size="sm"
                  color="secondary"
                  testId="dg-hazard-classification"
                >
                  {dangerousGoodsDescribeHazardClass(
                    dangerousClassification || "",
                  )}
                </T>
                {packingGroupSource !== "askCustomer" && (
                  <>
                    <T size="sm" color="muted">
                      ·
                    </T>
                    <T size="sm" color="secondary" testId="dg-packing-group">
                      {packingGroupSource === "lookedUp"
                        ? `Packing Group ${dangerousPackingGroup}`
                        : "No packing group"}
                    </T>
                  </>
                )}
              </Row>
            </Stack>
            {/* A link button, not an icon action: icon actions are not
                keyboard-reachable, and this is the only way back to the
                lookup step. */}
            <Button
              size="link"
              icon={PencilSimpleIcon}
              onClick={props.onChangeUnNumber}
              data-testid="dg-change-un-number"
            >
              Change
            </Button>
          </Row>
        </FormBox>

        <FormGroup label="Details we need from you" labelPosition="above">
          {packingGroupSource === "askCustomer" && (
            <FormItem
              label="Packing Group"
              error={errorMessageForPackingGroup()}
            >
              <Dropdown
                data-testid="dg-packing-group-dropdown"
                width="full"
                placeholder="Select a packing group"
                value={dangerousPackingGroup}
                options={[
                  { value: "I", label: "Packing Group I - High Danger" },
                  { value: "II", label: "Packing Group II - Medium Danger" },
                  { value: "III", label: "Packing Group III - Low Danger" },
                ]}
                onChange={function (value) {
                  props.onChange({ dangerousPackingGroup: value });
                }}
              />
            </FormItem>
          )}
          <Grid columns={2} gap="md">
            <FormItem
              label={
                <>
                  Package Count
                  <LineItemsNumberPackagesQuestionBubble />
                </>
              }
              error={errorMessageForNumberPackages()}
            >
              <TextInput
                data-testid="dg-package-count"
                value={dangerousNumberPackages?.toString() ?? ""}
                onChange={function (e) {
                  const value = e.target.value;
                  const packageRegex = new RegExp("^[1-9]\\d*$");
                  if (value === "") {
                    props.onChange({
                      dangerousNumberPackages: undefined,
                    });
                  }
                  // Refusing digits past the max also keeps the number short
                  // enough that it never renders in scientific notation,
                  // which this regex would then reject on every keystroke.
                  // Deleting is always allowed, so an over-max value that
                  // arrived from elsewhere can still be corrected.
                  const isDeleting =
                    value.length <
                    (dangerousNumberPackages?.toString() ?? "").length;
                  if (
                    value.match(packageRegex) &&
                    (parseInt(value) <= maxNumberPackages || isDeleting)
                  ) {
                    props.onChange({
                      dangerousNumberPackages: parseInt(value),
                    });
                  }
                }}
              />
            </FormItem>
            <FormItem
              label={
                <>
                  Packaging Type
                  <LineItemsPackagingTypeQuestionBubble />
                </>
              }
              error={errorMessageForPackagingType()}
            >
              <TextInput
                data-testid="dg-packaging-type"
                value={dangerousPackagingType ?? ""}
                onChange={function (e) {
                  props.onChange({ dangerousPackagingType: e.target.value });
                }}
              />
            </FormItem>
          </Grid>
          <FormItem
            label={
              <>
                {technicalNameRequired
                  ? "Technical Name"
                  : "Technical Name (Optional)"}
                <LineItemsTechnicalNameQuestionBubble
                  required={technicalNameRequired}
                />
              </>
            }
            error={errorMessageForTechnicalName()}
          >
            <TextInput
              data-testid="dg-technical-name"
              value={dangerousTechnicalName ?? ""}
              onChange={function (e) {
                props.onChange({ dangerousTechnicalName: e.target.value });
              }}
            />
          </FormItem>
          {collectNetExplosiveQuantity && (
            <FormItem
              label="Net Explosive Quantity (kg)"
              error={errorMessageForNetExplosiveQuantity()}
            >
              {/* Two decimal places to match the numeric(11,2) column, so a
                  value can't silently round on write. No min/max here — an
                  out-of-range value shows a validation error instead of being
                  silently clamped. */}
              <NumberInput
                data-testid="dg-net-explosive-quantity"
                value={dangerousNetExplosiveQuantityKg}
                setValue={function (value) {
                  props.onChange({ dangerousNetExplosiveQuantityKg: value });
                }}
                decimal
                maxDecimalPlaces={2}
                width="sm"
              />
            </FormItem>
          )}
        </FormGroup>
      </Stack>
    </ShowFormErrors>
  );
}
